import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Copy, Loader2, X } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import api, { formatApiError } from "@/lib/api";
import { useBackOffice } from "@/components/account/BackOffice";
import { Empty, Inline, Pills, Primary, Secondary, copyText, day, inputCls, money, useQuery } from "@/components/backoffice/kit";
import OrderCard from "@/components/backoffice/OrderCard";
import OrderDetail from "@/components/backoffice/OrderDetail";
import { ReadySheet } from "@/components/backoffice/sheets";

const SHOP_FILTERS = [["today", "Today"], ["open", "Open"], ["ready", "Ready"], ["done", "Done"]];
const PLATFORM_FILTERS = [["to_print", "To print"], ["all", "All shops"], ["ready", "Ready"], ["done", "Done"]];
const EMPTY = { today: "Nothing for today.", open: "Nothing open.", ready: "Nothing ready.", done: "Nothing done yet.", to_print: "Nothing to print.", all: "No orders yet.", proofs: "No proofs waiting." };

/** 8.2 — "Today: 6 to print · 2 ready for pickup · 1 proof waiting · Next payout $412 on Fri." Each count is a tap target. */
export function StatusLine({ counts, payout, onPick, platform = false, testid = "status-line" }) {
    if (!counts) return null;
    const btn = (key, n, label) => (
        <button onClick={() => onPick(key)} data-testid={`${testid}-${key}`} className="inline-flex min-h-[44px] items-center font-semibold underline">
            {n} {label}
        </button>
    );
    const pay = payout && (payout.next_amount != null
        ? `${platform ? "Platform payouts" : "Next payout"} ${money(payout.next_amount)}${payout.next_date ? ` on ${day(payout.next_date)}` : ""}`
        : `${platform ? "Platform payouts" : "Next payout"} —`);
    return (
        <p className="flex flex-wrap items-center gap-x-2 text-[16px]" data-testid={testid}>
            <span>{platform ? "To print:" : "Today:"}</span>
            {btn("to_print", counts.to_print, platform ? "" : "to print")} <span>·</span>
            {btn("ready", counts.ready, platform ? "ready" : "ready for pickup")} <span>·</span>
            {btn("proofs", counts.proofs_waiting, counts.proofs_waiting === 1 ? "proof waiting" : "proofs waiting")}
            {pay && <span className="text-[#4d4d4d]" data-testid={`${testid}-payout`}>· {pay}</span>}
        </p>
    );
}

export function FirstDayBanner({ url, onDismiss }) {
    const [copied, setCopied] = useState(false);
    return (
        <div className="relative grid gap-3 border border-[var(--accent)] bg-white p-4 sm:grid-cols-[auto_minmax(0,1fr)]" data-testid="first-day-banner">
            <button onClick={onDismiss} aria-label="Dismiss" data-testid="first-day-dismiss" className="absolute right-2 top-2 flex h-11 w-11 items-center justify-center border border-[#1a1a1a] bg-white"><X size={16} /></button>
            <div className="h-28 w-28 border border-[#1a1a1a] p-1"><QRCodeSVG value={url} size={104} /></div>
            <div className="min-w-0 pr-12">
                <p className="text-[18px] font-semibold">Your shop is open.</p>
                <p className="break-all text-[15px]" data-testid="first-day-link">{url}</p>
                <div className="mt-2 flex flex-wrap gap-2">
                    <Primary to={url.replace(window.location.origin, "")} testid="first-day-open">Open my shop</Primary>
                    <Secondary onClick={async () => setCopied(await copyText(url))} testid="first-day-copy">{copied ? <><Check size={16} /> Copied</> : <><Copy size={16} /> Copy link</>}</Secondary>
                </div>
            </div>
        </div>
    );
}

export function ShareEmpty({ url }) {
    const [copied, setCopied] = useState(false);
    return (
        <Empty title="No orders yet. Share your shop:" testid="orders-empty">
            <p className="mt-1 break-all text-[15px]" data-testid="orders-empty-link">{url}</p>
            <div className="mt-3 flex flex-wrap items-center gap-3">
                <Secondary onClick={async () => setCopied(await copyText(url))} testid="orders-empty-copy">{copied ? <><Check size={16} /> Copied</> : <><Copy size={16} /> Copy link</>}</Secondary>
                <div className="border border-[#1a1a1a] p-1"><QRCodeSVG value={url} size={96} /></div>
            </div>
        </Empty>
    );
}

/**
 * 8.2 reseller home: status line, first-day banner, Today / Open / Ready / Done, search, the cards. `?order=` opens 8.3.
 * 8.11 with `platform`: the production queue — To print / All shops / Ready / Done, a shop picker, the shop name on each card.
 */
export default function OrdersHome({ platform = false }) {
    const { data: bo } = useBackOffice();
    const { params, set, pathname } = useQuery();
    const base = platform ? "/admin/orders" : "/vendor/orders";
    const defaultFilter = platform ? "to_print" : "today";
    const filter = params.get("f") || defaultFilter;
    const q = params.get("q") || "";
    const shop = params.get("shop") || "";
    const orderId = params.get("order");
    const [sum, setSum] = useState(null);
    const [list, setList] = useState(null);
    const [err, setErr] = useState("");
    const [typed, setTyped] = useState(q);
    const [busy, setBusy] = useState(false);
    const [ready, setReady] = useState(null);

    const loadSummary = useCallback(() => api.get(`${base}/summary`).then(({ data }) => setSum(data)).catch(() => { }), [base]);
    const load = useCallback(() => {
        setErr("");
        api.get(`${base}/gallery`, { params: { filter, q, ...(shop ? { shop } : {}) } }).then(({ data }) => setList(data))
            .catch((e) => { setList({ orders: [] }); setErr(formatApiError(e.response?.data?.detail)); });
    }, [base, filter, q, shop]);
    useEffect(() => { loadSummary(); }, [loadSummary]);
    useEffect(() => { if (!orderId) load(); }, [load, orderId]);
    useEffect(() => { setTyped(q); }, [q]);

    const keep = platform ? { tab: "orders" } : {};
    const pick = (key) => set({ ...keep, f: key, order: null });
    const advance = async (order, status) => {
        if (status === "ready") return setReady(order);
        setBusy(true);
        try {
            await api.post(`${base}/${order.id}/advance`, { status });
            toast.success(status === "in_production" ? "Printing" : "Done");
            load(); loadSummary();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
    };
    const dismiss = async () => { await api.post(`${base}/first-day/dismiss`).catch(() => { }); setSum((s) => ({ ...s, first_day: { ...s.first_day, show: false } })); };

    if (orderId) {
        const p = new URLSearchParams(params); p.delete("order");
        return <OrderDetail orderId={orderId} platform={platform} back={`${pathname}${p.toString() ? `?${p}` : ""}`} />;
    }
    const shopUrl = sum?.shop_url || `${window.location.origin}/stores/${bo?.store?.slug || ""}`;
    const counts = list?.counts || sum?.counts;
    const rows = list?.orders || [];
    const shopName = shop ? (sum?.shops || []).find((s) => s.slug === shop)?.name : "";
    const baseFilters = platform ? PLATFORM_FILTERS : SHOP_FILTERS;
    const extra = baseFilters.some(([k]) => k === filter) ? [] : [[filter, { to_print: "To print", proofs: "Proofs waiting" }[filter] || filter]];
    const pillItems = [...extra, ...baseFilters].map(([k, l]) => ({ key: k, label: k === "all" && shopName ? shopName : l, count: counts ? counts[k] : null }));
    const cardLink = (o) => `${pathname}?${new URLSearchParams({ ...keep, ...(filter !== defaultFilter ? { f: filter } : {}), ...(q ? { q } : {}), ...(shop ? { shop } : {}), order: o.id })}`;

    return (
        <div className="grid gap-4" data-testid="orders-home">
            <StatusLine counts={counts} payout={sum?.payout} onPick={pick} platform={platform} />
            {sum?.first_day?.show && <FirstDayBanner url={sum.first_day.url} onDismiss={dismiss} />}
            <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(200px,320px)]">
                <Pills items={pillItems} active={filter} onPick={(k) => set({ ...keep, f: k })} testid="orders-filter" />
                <form onSubmit={(e) => { e.preventDefault(); set({ ...keep, q: typed.trim() }); }} className="flex gap-2">
                    <input className={inputCls} value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={platform ? "Code, name, phone or shop" : "Code, name or phone"} aria-label="Search orders" data-testid="orders-search" />
                    <Secondary type="submit" testid="orders-search-go">Search</Secondary>
                </form>
            </div>
            {platform && (
                <label className="flex flex-wrap items-center gap-2 text-[15px]">
                    <span className="font-semibold">Shop</span>
                    <select className={`${inputCls} !w-auto`} value={shop} onChange={(e) => set({ ...keep, shop: e.target.value })} data-testid="orders-shop-picker">
                        <option value="">Every shop</option>
                        {(sum?.shops || []).map((s) => <option key={s.slug} value={s.slug}>{s.name} ({s.count})</option>)}
                    </select>
                </label>
            )}
            <Inline error={err} onRetry={load} testid="orders-error" />
            {list === null ? (
                <div className="flex min-h-[30vh] items-center justify-center" data-testid="orders-loading"><Loader2 className="animate-spin" /></div>
            ) : rows.length === 0 ? (
                !platform && counts && counts.open + counts.done === 0 && !q
                    ? <ShareEmpty url={shopUrl} />
                    : <Empty title={q ? `Nothing matches “${q}”.` : (EMPTY[filter] || "Nothing here.")} testid="orders-none">
                        {q && <button onClick={() => set({ ...keep, q: "" })} className="mt-1 min-h-[44px] underline" data-testid="orders-clear-search">Clear search</button>}
                        {!q && counts?.open > 0 && filter !== "open" && filter !== "all" && (
                            <button onClick={() => pick(platform ? "all" : "open")} className="mt-1 min-h-[44px] underline" data-testid="orders-see-open">
                                {platform ? `See all ${counts.all} orders` : `See ${counts.open} open order${counts.open === 1 ? "" : "s"}`}
                            </button>
                        )}
                    </Empty>
            ) : (
                <ul className="grid gap-3 lg:grid-cols-2" data-testid="orders-gallery">
                    {rows.map((o) => (
                        <li key={o.id} className="h-full">
                            <OrderCard order={o} to={cardLink(o)} onAdvance={advance} busy={busy} />
                        </li>
                    ))}
                </ul>
            )}
            {ready && <ReadySheet order={ready} base={base} pickupMode={platform ? (sum?.store?.pickup_mode || "locker") : (bo?.store?.pickup_mode || "locker")} onDone={() => { setReady(null); toast.success("Ready — the customer has been told"); load(); loadSummary(); }} onClose={() => setReady(null)} />}
        </div>
    );
}
